export interface Shortcut {
  /** Unique id like "platform:search" or "accounting:journal:new" */
  id: string;
  /** Key sequence: single key ("n"), modified ("Cmd+K"), or sequence ("g a") */
  keys: string;
  /** Scope namespace controls conflict detection */
  scope: string;
  /** i18n key OR literal label for the help overlay */
  label: string;
  /** Optional sensitivity flag */
  sensitive?: boolean;
}

export class ShortcutRegistry {
  private byId = new Map<string, Shortcut>();
  private byScopeAndKeys = new Map<string, string>(); // "scope|keys" → id

  register(s: Shortcut): void {
    if (this.byId.has(s.id)) {
      throw new Error(`Shortcut id "${s.id}" already registered`);
    }
    const conflictKey = `${s.scope}|${s.keys}`;
    const existingId = this.byScopeAndKeys.get(conflictKey);
    if (existingId) {
      throw new Error(
        `Shortcut keys "${s.keys}" conflict with "${existingId}" in scope "${s.scope}"`,
      );
    }
    this.byId.set(s.id, s);
    this.byScopeAndKeys.set(conflictKey, s.id);
  }

  unregister(id: string): void {
    const s = this.byId.get(id);
    if (!s) return;
    this.byId.delete(id);
    this.byScopeAndKeys.delete(`${s.scope}|${s.keys}`);
  }

  get(id: string): Shortcut | undefined {
    return this.byId.get(id);
  }

  list(): Shortcut[] {
    return [...this.byId.values()];
  }

  listByScope(scope: string): Shortcut[] {
    return this.list().filter((s) => s.scope === scope);
  }
}
